import React, { useEffect, useState } from 'react';
import { TableComponent } from '../../../components/TableComponent';
import { MerchantFeesTableRow } from '../../../components/merchants/MerchantFeesTableRow';
import { useMerchants } from '../../../store/merchnts/merchants-context';
import { Dropdown } from 'rsuite';
import { IFees } from '../../../store/merchnts/merchant-interfaces';
import { setFeesData } from '../../../store/merchnts/actions/fees';
import { SectionWrapper } from '../../../components/SectionWrapper';
import { useTranslation } from 'react-i18next';
import { fetchMerchantData, setMerchantDetailsData } from '../../../store/merchnts/actions/merchant';
import { Link, useParams, useLocation } from 'react-router-dom';
import { isEqual } from 'lodash';
import { IModalWindow, ModalWindow } from '../../../components/modal/ModalWindow';
import { MERCHANTS_TABS } from '../../../utils/merchantUtils';

const feeTableColumn = [
	{
		key: 'name',
		title: 'Fee Name',
		additionalColumnClassName: 'col-2',
	},
	{
		key: 'value',
		title: 'Fee Value',
		additionalColumnClassName: 'col-2',
	},
	{
		key: 'minFeeAmount',
		title: 'Min Fee Amount',
		additionalColumnClassName: 'col-2',
	},
	{
		key: 'action',
		title: 'MERCHANTS_LIST.ACTION',
		additionalColumnClassName: 'w-100px',
	}
];

export const MerchantFeesPage = (): JSX.Element => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const { pathname, state } = useLocation();
	const path = pathname.split('/')[4];
	const { merchantState: { isMerchantDataLoading, merchantFees, defaultFees, merchantData }, dispatch } = useMerchants();
	const [newFee, setNewFee] = useState<IFees[]>([]);
	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [newMerchantFees, setNewMerchantFees] = useState<IFees[]>([]);
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

	const onFeeUpdate = (currentFee: IFees, index: number): void => {
		const newData = [...newMerchantFees];
		newData.splice(index, 1 , currentFee);
		// setFeesData(dispatch, newData);
		setNewMerchantFees(newData);
	};

	const showAddBtn = defaultFees.length === merchantFees.length;

	useEffect(() => {
		setNewMerchantFees(merchantFees);
		if (defaultFees.length || merchantFees.length) {
			if (showAddBtn) {
				return;
			}
			const merchantFeesNames: string[] = merchantFees.map((fee) => fee.name);
			const arr = defaultFees?.filter(fee => !merchantFeesNames.includes(fee.name));
			setNewFee(arr);
		}
	}, [merchantFees, defaultFees]);

	const renderButton = (props, ref): JSX.Element => {
		return (
			<button
				{...props}
				ref={ref}
				className="btn-primary btn btn-sm fw-bolder letter-spacing"
				disabled={!isEditMode || showAddBtn}
			>
				Add
			</button>
		);
	};

	const handleSelect = (id): void => {
		const selectedFee = newFee?.find(fee => fee.id === id);
		const newData = [...merchantFees];
		newData.push(selectedFee);
		setFeesData(dispatch, newData);
	};

	const dropdown = (
		<Dropdown renderToggle={renderButton} placement="bottomEnd">
			{
				newFee.map(fee => (
					<Dropdown.Item
						key={fee?.id}
						onClick={(): void => handleSelect(fee?.id)}
					>
						{fee?.name}
					</Dropdown.Item>
				))
			}
		</Dropdown>
	);

	const addedFeesValue = (): IFees[] => {
		return newMerchantFees.map((fee: IFees) => {
			return {
				amount: Math.round(fee.amount * 100),
				minimumFeeAmount: fee.minimumFeeAmount !== '' ? Math.round(Number(fee.minimumFeeAmount) * 100) : null,
				id: fee.id,
				name: fee.name
			};
		});
	};

	const handleEditClick = async (): Promise<void> => {
		try {
			const params = {
				fees: addedFeesValue()
			};

			if (!isEqual(merchantFees, newMerchantFees)) {
				setIsLoading(true);
				await setMerchantDetailsData(id, params, path);
				await fetchMerchantData(dispatch, id);
			}
			setIsEditMode(!isEditMode);
			setIsLoading(false);
		} catch (e) {
			console.log('handleEditClick error', e);
			setIsLoading(false);
		}
	};

	const handleCancelClick = (): void => {
		if (!isEqual(merchantFees, newMerchantFees)) {
			setModalState({
				isOpen: true,
				modalTitle: t('MERCHANT.CANCEL_MODAL_TITLE'),
				modalText: t('MERCHANT.CANCEL_MODAL_TEXT'),
				confirmButtonText: t('MERCHANT.CANCEL_MODAL_CONFIRM_BTN_NAME'),
				onConfirm: closeModal,
				onCancel: (): void => {
					closeModal();
					setIsEditMode(false);
					fetchMerchantData(dispatch, id);
				}
			});
		} else {
			setIsEditMode(false);
		}
	};

	const showPlaceholder = !merchantData || typeof merchantData === 'object' && Object.keys(merchantData).length === 0 || typeof merchantData !== 'object';

	return (
		<>
			<div className="ms-8 mb-5">
				<Link
					to={`/accounts/merchants/${id}/${MERCHANTS_TABS.FEES_HISTORY}`}
					state={{ pageNumber: state?.pageNumber, pageSize: state?.pageSize }}
					className="text-danger fs-2 cursor-pointer fc-merchant-name-link"
				>
					{t('MERCHANT.FEES_HISTORY')}
				</Link>
			</div>

			<SectionWrapper
				showPlaceholder={showPlaceholder}
				title={t('MERCHANT.FEES')}
				sectionAdditionalClass="mb-5 min-height-400"
				isEditMode={isEditMode}
				setIsEditMode={setIsEditMode}
				onEditClick={handleEditClick}
				onCancelClick={handleCancelClick}
				loading={isLoading}
				pageLoading={isMerchantDataLoading}
				isEditBlock={!showPlaceholder}
			>
				<div className="card-body d-flex justify-content-end">
					{dropdown}
				</div>
				<TableComponent
					column={feeTableColumn}
					tableData={merchantFees}
					getRowComponent={(row: IFees, index): JSX.Element => (
						<MerchantFeesTableRow
							key={row?.id}
							currentFee={row}
							index={index}
							formEditStatus={isEditMode}
							onFeeUpdate={onFeeUpdate}
							setDefault
						/>
					)}
				/>
			</SectionWrapper>
			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>
		</>
	);
};
